import { isNil, minBy } from "lodash"
import { Q_FROM_ORDINAL, Q_ORDINAL, Q_VENUE } from "constant"
import { pluralize } from "utils/string"
import { useVenueController } from "hooks/venue"
import { useVenue } from "providers/venue"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useFeatureQuickSearch } from "./useFeatureQuickSearch"
import { IFeatureCategoryObj } from "./useFeatureQuickSearch/types"
import { Feature, Geometry, GeometryCollection } from "@turf/helpers"

export interface IUseFeatureCategoryProps {
  initialVenueId?: string
  initialOrdinal?: number
}

export type ToggleQuickSearchFn = (
  type: string,
  featureCategory: string
) => void
export type HandleClickQuickSearchFn = (
  featureType: string,
  category: string
) => void
export type UseFeatureCategory = (props?: IUseFeatureCategoryProps) => {
  preFilterReady: boolean
  viewingCategory: string
  featureCategories: IFeatureCategoryObj[]
  searchableFeatures: Feature<
    Geometry | GeometryCollection,
    {
      [name: string]: any
    }
  >[]
  viewingOrdinal: string | number
  viewingVenueId: string
  comingFromOrdinal: string | number
  toggleQuickSearch: ToggleQuickSearchFn
  handleClickQuickSearch: HandleClickQuickSearchFn
  handleOrdinalChange: (data: { ordinal: number; venueId: string }) => void
}

export const useFeatureCategory: UseFeatureCategory = ({
  initialVenueId,
  initialOrdinal,
}) => {
  const { category: viewingCategory } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [preFilterReady, setPreFilterReady] = useState<boolean>(false)
  const { dataLoaded } = useVenue()

  const { viewingVenue: viewingVenueId, changeViewingVenue } =
    useVenueController(initialVenueId)
  const { categories: featureCategories, features: searchableFeatures } =
    useFeatureQuickSearch(viewingCategory, viewingVenueId)

  const viewingOrdinal = useMemo(
    () => searchParams.get(Q_ORDINAL),
    [searchParams]
  )

  const comingFromOrdinal = useMemo(
    () => searchParams.get(Q_FROM_ORDINAL),
    [searchParams]
  )

  const toggleQuickSearch = useCallback(
    (type, featureCategory) => {
      //This condition prevent navigation when viewingOrdinal or viewingVenueId is undefined
      const hasEmptyValue = [viewingVenueId, viewingOrdinal].some(isNil)
      if (hasEmptyValue) return
      if (featureCategory === viewingCategory) {
        return navigate(
          `/maps/ordinal/${viewingOrdinal}?venue=${viewingVenueId}`
        )
      }
      navigate(
        `/maps/${type}/${featureCategory}?venue=${viewingVenueId}&from-ordinal=${viewingOrdinal}`
      )
    },
    [navigate, viewingOrdinal, viewingCategory, viewingVenueId]
  )

  const handleClickQuickSearch = useCallback(
    (featureType, category) => {
      const pluralType = pluralize(featureType)
      toggleQuickSearch(pluralType, category)
    },
    [toggleQuickSearch]
  )

  const handleOrdinalChange = useCallback(
    ({ ordinal, venueId }) => {
      searchParams.set(Q_ORDINAL, ordinal)
      searchParams.set(Q_VENUE, venueId)
      setSearchParams(searchParams.toString(), {
        replace: true,
      })
    },
    [searchParams, setSearchParams]
  )

  const findNearestOrdinal = useCallback(
    (viewingOrdinal) => {
      const parsedOrdinal = !isNil(viewingOrdinal) ? Number(viewingOrdinal) : 0
      let nearestOrdinal = parsedOrdinal
      const nearestFeature = minBy(searchableFeatures, (amenity) =>
        Math.abs(amenity.properties.ordinal - parsedOrdinal)
      )
      if (nearestFeature) {
        nearestOrdinal = nearestFeature.properties.ordinal
      }

      return nearestOrdinal
    },
    [searchableFeatures]
  )

  // Runs on initial page load
  useEffect(() => {
    // Skip effect if ordinal and venue are already set on page reload
    if (preFilterReady || !dataLoaded) return

    // Skip effect without ordinal query string
    if (isNil(viewingOrdinal)) return

    const currentVenueIdFilterInPage = searchParams.get(Q_VENUE)
    changeViewingVenue(currentVenueIdFilterInPage)
    setSearchParams(
      { venue: currentVenueIdFilterInPage, ordinal: viewingOrdinal },
      { replace: true }
    )
    setPreFilterReady(true)
  }, [
    searchParams,
    changeViewingVenue,
    dataLoaded,
    preFilterReady,
    viewingOrdinal,
    viewingVenueId,
    setSearchParams,
    setPreFilterReady,
  ])

  // Runs when coming from Amenity Quick Search
  useEffect(() => {
    if (!viewingVenueId || !dataLoaded || viewingOrdinal) return

    /**
     * Find the nearest ordinal for amenity:
     * - If `comingFromOrdinal` is present, use it (applies when navigating from another page).
     * - If `viewingOrdinal` is missing and `comingFromOrdinal` is absent, but `initialOrdinal` exists, use `locationOrdinal`.
     * - Otherwise, default to 0 (GF).
     */
    const currentOrdinal = comingFromOrdinal || initialOrdinal || 0
    const nearestOrdinal = findNearestOrdinal(currentOrdinal)?.toString()
    setSearchParams(
      { venue: viewingVenueId, ordinal: nearestOrdinal },
      { replace: true }
    )
  }, [
    dataLoaded,
    viewingCategory,
    comingFromOrdinal,
    viewingVenueId,
    viewingOrdinal,
    initialOrdinal,
    findNearestOrdinal,
    setSearchParams,
  ])

  return {
    preFilterReady,
    featureCategories,
    searchableFeatures,
    viewingCategory,
    viewingOrdinal,
    viewingVenueId,
    comingFromOrdinal,
    toggleQuickSearch,
    handleClickQuickSearch,
    handleOrdinalChange,
  }
}
